/**
 * The single source of verdict rendering. Every pass/warn/fail/na in the
 * product renders through this component.
 *
 * That is a hard constraint, not a preference: forty of these share a screen in
 * the compliance matrix, so one inconsistency is immediately visible, and
 * verdict rendering is the product's core visual vocabulary.
 *
 * ACCESSIBILITY RULE (DESIGN-SYSTEM §1): status is never conveyed by colour
 * alone. Label first, glyph second, colour third. The tinted fill is decorative
 * — do not remove the text label to save space.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type Verdict = 'pass' | 'warn' | 'fail' | 'na' | 'info';

const LABELS: Record<Verdict, string> = {
  pass: 'Compliant',
  warn: 'Attention',
  fail: 'Gap',
  na: 'N/A',
  info: 'Info',
};

/* Fixed vocabulary — see BRAND.md §4. Do not paraphrase per screen. */
const RGB_VAR: Record<Verdict, string> = {
  pass: '--success-rgb',
  warn: '--warn-rgb',
  fail: '--danger-rgb',
  na: '--na-rgb',
  info: '--info-rgb',
};

const FG_VAR: Record<Verdict, string> = {
  pass: '--success',
  warn: '--warn',
  fail: '--danger',
  na: '--muted-fg',
  info: '--info',
};

/**
 * TEXT colour, which is not the same thing as the verdict colour.
 *
 * The badge painted its label in `FG_VAR` over a 12% tint of the same token. Measured on the
 * composite in light theme that is 4.25:1 for `warn` — under the 4.5:1 floor — and green is
 * worse: `--success` #16a34a is only 3.36:1 on plain white before any tint. The label is the
 * word "Warn"/"Pass"/"Fail" itself, so it is the part that must be readable; the tint, the
 * border and the glyph carry the colour and are graphics, held to 3:1 by WCAG 1.4.11.
 *
 * In dark theme the ink tokens resolve to the same bright hues, because on a dark tint they
 * already clear 4.5:1 — see tokens.css.
 */
const INK_VAR: Record<Verdict, string> = {
  pass: '--success-ink',
  warn: '--warn-ink',
  fail: '--danger-ink',
  na: '--muted-fg',
  info: '--info-ink',
};

function Glyph({ verdict }: { verdict: Verdict }): ReactNode {
  const common = {
    width: 12,
    height: 12,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    style: { flex: 'none' },
  };

  if (verdict === 'pass') {
    return (
      <svg {...common}>
        <path d="M20 6 9 17l-5-5" />
      </svg>
    );
  }
  if (verdict === 'fail') {
    return (
      <svg {...common}>
        <path d="M12 9v4M12 17h.01" />
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      </svg>
    );
  }
  if (verdict === 'warn') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v4M12 16h.01" />
      </svg>
    );
  }
  return null;
}

export interface StatusBadgeProps {
  verdict: Verdict;
  /** Overrides the fixed label. Use sparingly — the vocabulary is deliberate. */
  label?: string;
  className?: string;
}

export function StatusBadge({
  verdict,
  label,
  className,
}: StatusBadgeProps): ReactNode {
  const text = label ?? LABELS[verdict];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-[5px] rounded-[var(--radius-pill)]',
        'px-[9px] py-[2px] text-[11.5px] font-semibold whitespace-nowrap',
        className,
      )}
      style={{
        background: `rgb(var(${RGB_VAR[verdict]}) / 12%)`,
        border: `1px solid rgb(var(${RGB_VAR[verdict]}) / 25%)`,
        color: `var(${INK_VAR[verdict]})`,
      }}
    >
      <Glyph verdict={verdict} />
      {text}
    </span>
  );
}

/** 8px dot with an alpha ring, for dense lists where a badge will not fit. */
export function StatusDot({
  verdict,
  className,
}: {
  verdict: Verdict;
  className?: string;
}): ReactNode {
  return (
    <span
      role="img"
      aria-label={LABELS[verdict]}
      className={cn('inline-block size-2 shrink-0 rounded-full', className)}
      style={{
        background: `var(${FG_VAR[verdict]})`,
        boxShadow: `0 0 0 3px rgb(var(${RGB_VAR[verdict]}) / 15%)`,
      }}
    />
  );
}
